import { GridColDef, GridColumnGroupingModel, GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ReportCustomTemplateField, ReportCustomTemplateFieldTypes, ReportCustomTemplateFieldWithName, ReportTemplateTypes } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import useReportCustomTemplateFields from "../../hooks/data/useReportCustomTemplateFields";

export const useTemplateFieldColumns = (fields?: ReportCustomTemplateField[]) => {
    const {data: fieldsWithNames} = useReportCustomTemplateFields();

    const fieldColumns = useMemo(() => {

        const getFieldSpecifier = (field: ReportCustomTemplateFieldWithName) => {
            return field.field.fieldType == ReportCustomTemplateFieldTypes.LotProperty 
                ? field.name 
                : field.field.fieldType
                ? ReportCustomTemplateFieldTypes[field.field.fieldType]
                : "";
        }

        const getValueGetter = (fieldType?: ReportCustomTemplateFieldTypes, value?: any) => {
            switch(fieldType) {
                case ReportCustomTemplateFieldTypes.ContractCommission:
                case ReportCustomTemplateFieldTypes.ContractDeposit:
                case ReportCustomTemplateFieldTypes.ContractLegalFees:
                case ReportCustomTemplateFieldTypes.ContractRateAdjustments:
                case ReportCustomTemplateFieldTypes.ContractRebate:
                case ReportCustomTemplateFieldTypes.ContractGst:
                case ReportCustomTemplateFieldTypes.ContractPrice:
                case ReportCustomTemplateFieldTypes.ContractWaterAdjustments:
                case ReportCustomTemplateFieldTypes.LotAdvertisedPrice:
                case ReportCustomTemplateFieldTypes.LotApprovedRebate:
                case ReportCustomTemplateFieldTypes.LotLandValue:
                case ReportCustomTemplateFieldTypes.ProjectCostPerLot:
                case ReportCustomTemplateFieldTypes.ProjectDefaultCommission:
                case ReportCustomTemplateFieldTypes.ProjectPercentShare:
                case ReportCustomTemplateFieldTypes.ProjectValuation:
                case ReportCustomTemplateFieldTypes.StageArea:
                case ReportCustomTemplateFieldTypes.StageBudget:
                case ReportCustomTemplateFieldTypes.StageCostPerLot:
                    const val = Math.round(Number(value)*100)/100;
                    return Number.isNaN(val) ? undefined : val;

                case ReportCustomTemplateFieldTypes.ContractActualSettleDate:
                case ReportCustomTemplateFieldTypes.ContractDateBackOnMarket:
                case ReportCustomTemplateFieldTypes.ContractExchangeDate:
                case ReportCustomTemplateFieldTypes.ContractExpectedSettleDate:
                case ReportCustomTemplateFieldTypes.ContractIssueDate:
                case ReportCustomTemplateFieldTypes.LotDateOnMarket:
                case ReportCustomTemplateFieldTypes.LotLandValueDate:
                case ReportCustomTemplateFieldTypes.ProjectValuationDate:
                case ReportCustomTemplateFieldTypes.StageRegistrationDate:
                    return value ? DateTime.fromFormat(value, "dd/MM/yyyy").toJSDate() : value;
                default:
                    return value;
            }
        }

        const getValueFormatter = (fieldType?: ReportCustomTemplateFieldTypes, value?: any) => {
            switch(fieldType) {
                case ReportCustomTemplateFieldTypes.ContractCommission:
                case ReportCustomTemplateFieldTypes.ContractDeposit:
                case ReportCustomTemplateFieldTypes.ContractLegalFees:
                case ReportCustomTemplateFieldTypes.ContractRateAdjustments:
                case ReportCustomTemplateFieldTypes.ContractRebate:
                case ReportCustomTemplateFieldTypes.ContractGst:
                case ReportCustomTemplateFieldTypes.ContractPrice:
                case ReportCustomTemplateFieldTypes.ContractWaterAdjustments:
                case ReportCustomTemplateFieldTypes.LotAdvertisedPrice:
                case ReportCustomTemplateFieldTypes.LotApprovedRebate:
                case ReportCustomTemplateFieldTypes.LotLandValue:
                case ReportCustomTemplateFieldTypes.ProjectCostPerLot:
                case ReportCustomTemplateFieldTypes.ProjectDefaultCommission:
                case ReportCustomTemplateFieldTypes.ProjectPercentShare:
                case ReportCustomTemplateFieldTypes.ProjectValuation:
                case ReportCustomTemplateFieldTypes.StageArea:
                case ReportCustomTemplateFieldTypes.StageBudget:
                case ReportCustomTemplateFieldTypes.StageCostPerLot:
                    const val = Math.round(Number(value)*100)/100;
                    return Number.isNaN(val) ? undefined : val;
                default:
                    return value;
            }
        }

        const columns: GridColDef[] = fieldsWithNames?.filter(fieldWithName => 
            (fields?.filter(field => 
                field.fieldType == fieldWithName.field.fieldType &&
                field.fieldReferenceId == fieldWithName.field.fieldReferenceId
            ).length ?? -1) > 0
        ).map(field => {
            return {
                headerName: field.name,
                field: getFieldSpecifier(field) ?? '',
                valueGetter: (_, row) => getValueGetter(field.field.fieldType, row[getFieldSpecifier(field)]),
                valueFormatter: (_, row) => getValueFormatter(field.field.fieldType, row[getFieldSpecifier(field)]),
                minWidth: 100,
                flex: 1
            };
        }) ?? [];
        
        return columns;
    }, [fieldsWithNames, fields]);

    return fieldColumns;
}
export const useAssetsColumns = () => {
    const params = useParams();

    const reportId = Number(params.reportId);
    const templateId = Number(params.templateId);

    const formUrlBase = reportId 
        ? `reports/preview/${reportId}`
        : `reports/templates/preview/${templateId}`;

    const assetsColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'Name',
                headerName: 'Asset Name',
                flex: 1
            },
            {
                field: 'Address',
                headerName: 'Address',
                flex: 1
            },
            {
                field: 'BankValuation',
                headerName: 'Bank Valuation',
                flex: 1,
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
            },
            {
                field: 'BankValuationDate',
                headerName: 'Bank Valuation Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.BankValuationDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.BankValuationDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'DirectorsValuation',
                headerName: 'Directors Valuation',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
            },
            {
                field: 'DirectorsValuationDate',
                headerName: 'Directors Valuation Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.DirectorsValuationDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.DirectorsValuationDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'LoanAccountId',
                headerName: 'Loan Account ID',
                flex: 1
            },
            {
                field: 'actions',
                headerName: '',
                renderCell: (params) => (
                    <GridActions
                        editUrl={`/${formUrlBase}/assets/edit/${params.row.id}`}
                        deleteUrl={`/${formUrlBase}/assets/delete/${params.row.id}`}
                    />
                ),
                disableExport: true
            }
        ]

        return columns;
    }, [formUrlBase])

    return assetsColumns;
}

export const useSalesSummaryColumns = (rows: {[index: string]: string}[]) => {
    const salesSummaryColumns = useMemo(() => {
        const defaultKeys = [
            "Month", 
            "LotsExchanged", 
            "TotalContractValue", 
            "AverageLotPrice"
        ];
        const columns = rows.reduce<GridColDef[]>((colDefs, row) => {
            Object.keys(row).forEach(key => {
                    if(defaultKeys.includes(key) || colDefs.filter(def => def.field == key).length > 0) {
                        return;
                    }
                    
                    colDefs.push({
                        field: key,
                        headerName: key,
                        flex: 1
                    })
                }
            );

            return colDefs;
        }, []);
        const returnColumns: GridColDef[] = [
            
            {
                field: 'Month',
                headerName: 'Month',
                flex: 1
            },
            {
                field: 'LotsExchanged',
                headerName: 'No. of Standard Residential Lots Exchanged',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            },
            {
                field: 'TotalContractValue',
                headerName: 'Total Contract Value',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'AverageLotPrice',
                headerName: 'Average Price / Lot',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            ...columns
        ]
        return returnColumns;
    }, [rows])

    return salesSummaryColumns;
}

export const useLotSummaryColumns = () => {
    const lotSummaryColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'Field',
                headerName: '',
                flex: 1,
            },
            {
                field: 'Value',
                headerName: '',
                flex: 1
            }
        ]

        return columns;
    }, [])

    return lotSummaryColumns;
}

export const useContractsOverdueColumns = () => {
    const contractsOverdueColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'ProjectName',
                headerName: 'Project',
                flex: 1
            },
            {
                field: 'StageName',
                headerName: 'Stage',
                flex: 1
            },
            {
                field: 'LotNumber',
                headerName: 'Lot',
                flex: 1
            },
            {
                field: 'OverdueType',
                headerName: 'Overdue',
                flex: 1
            },
            {
                field: 'ContractPrice',
                headerName: 'Contract Price',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'IssueDate',
                headerName: 'Issue Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.IssueDate).toJSDate();
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.IssueDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'DueDate',
                headerName: 'Due Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.DueDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.DueDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'DaysOverdue',
                headerName: 'Days Overdue',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            },

        ]

        return columns;
    }, []);

    return contractsOverdueColumns;
}

export const useCommissionReportColumns = () => {
    const commissionReportColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'LotNumber',
                headerName: 'Lot',
                flex: 1
            },
            {
                field: 'ContractPrice',
                headerName: 'Contract Price',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'ExchangeDate',
                headerName: 'Exchange Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.ExchangeDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.ExchangeDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'SettleDate',
                headerName: 'Settle Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.SettleDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.SettleDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            },
            {
                field: 'Commission',
                headerName: 'Commission',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'CommissionGst',
                headerName: 'GST on Commission',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'TotalCommission',
                headerName: 'Total Commission',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'PaidOn',
                headerName: 'Paid On',
                flex: 1
            }
        ]

        return columns;
    }, [])

    return commissionReportColumns;
}

export const useSalesAdviceToContractColumns = () => {
    const salesAdviceToContractColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'ProjectName',
                headerName: 'Project',
                flex: 1
            },
            {
                field: 'AverageDays',
                headerName: 'Average Days',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            }
        ]

        return columns;
    }, []);

    return salesAdviceToContractColumns;
}

export const useGrossRealisationColumns = (rows: {[index: string]: string}[]) => {
    const grossRealisationColumns = useMemo(() => {
        const defaultKeys = [
            "ProjectId", 
            "ProjectName", 
            "ShortAverage", 
            "ShortIncrease", 
            "MediumAverage",
            "MediumIncrease",
            "LongAverage",
            "LongIncrease"
        ];
        const columns = rows.reduce<GridColDef[]>((colDefs, row) => {
            Object.keys(row).forEach(key => {
                    if(defaultKeys.includes(key) || colDefs.filter(def => def.field == key).length > 0) {
                        return;
                    }
                    
                    colDefs.push({
                        field: key,
                        headerName: key,
                        minWidth: 120
                    })
                }
            );

            return colDefs;
        }, []);

        return [
            {
                field: 'ProjectName',
                headerName: 'Project Name',
                minWidth: 300
            },
            ...columns.sort((a, b) => a.field < b.field ? -1 : 1),
            {
                field: 'ShortAverage',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                minWidth: 100
            },
            {
                field: 'ShortIncrease',
                headerName: '% Increase',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'MediumAverage',
                headerName: 'Average',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                minWidth: 100
            },
            {
                field: 'MediumIncrease',
                headerName: '% Increase',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'LongAverage',
                headerName: 'Average',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                minWidth: 100
            },
            {
                field: 'LongIncrease',
                headerName: '% Increase',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },

        ] as GridColDef[];
    }, [rows]);

    return grossRealisationColumns;
}

export const useSalesRateColumns = (rows: {[index: string]: string}[]) => {
    const salesRateColumns = useMemo(() => {
        const defaultKeys = [
            "ProjectId", 
            "ProjectName", 
            "ShortMonth", 
            "ShortPa", 
            "MediumMonth",
            "MediumPa",
            "LongMonth",
            "LongPa"
        ];
        const columns = rows.reduce<GridColDef[]>((colDefs, row) => {
            Object.keys(row).forEach(key => {
                    if(defaultKeys.includes(key) || colDefs.filter(def => def.field == key).length > 0) {
                        return;
                    }
                    
                    colDefs.push({
                        field: key,
                        headerName: key,
                        minWidth: 120
                    })
                }
            );

            return colDefs;
        }, []);

        return [
            {
                field: 'ProjectName',
                headerName: 'Project Name',
                minWidth: 300
            },
            ...columns.sort((a, b) => a.field < b.field ? -1 : 1),
            {
                field: 'ShortPa',
                headerName: 'Annual',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'ShortMonth',
                headerName: 'Monthly',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'MediumPa',
                headerName: 'Annual',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'MediumMonth',
                headerName: 'Monthly',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'LongPa',
                headerName: 'Annual',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            },
            {
                field: 'LongMonth',
                headerName: 'Monthly',
                type: "number",
                headerAlign: "left",
                align: "left",
                minWidth: 100
            }
        ] as GridColDef[];
    }, [rows]);

    return salesRateColumns;
}

export const useStageLotCountsColumns = () => {
    const stageLotCountsColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'PrecinctName',
                headerName: 'Precinct',
                flex: 1
            },
            {
                field: 'StageName',
                headerName: 'Stage Name',
                flex: 1
            },
            {
                field: 'DaApprovedLots',
                headerName: 'DA Approved Residential Lots',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            },
            {
                field: 'ResidentialSites',
                headerName: 'Residential Development Sites',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            },
            {
                field: 'ReferStage',
                headerName: 'Potential Lot Yield',
                flex: 1
            },
            {
                field: 'SpecialLots',
                headerName: 'Special Lots',
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1
            },
            {
                field: 'Comments',
                headerName: 'Comments',
                flex: 1
            },
        ]

        return columns;
    }, [])

    return stageLotCountsColumns;
}

export const useUpcomingSettlementsColumns = () => {
    const upcomingSettlementColumns = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'ProjectName',
                headerName: 'Project',
                flex: 1
            },
            {
                field: 'StageName',
                headerName: 'Stage',
                flex: 1
            },
            {
                field: 'LotNumber',
                headerName: 'Lot',
                flex: 1
            },
            {
                field: 'Amount',
                headerName: 'Amount',
                type: "number",
                headerAlign: "left",
                align: "left",
                renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                },
                flex: 1
            },
            {
                field: 'ExpectedSettleDate',
                headerName: 'Date',
                type: "date",
                valueGetter: (_value, row) => {
                    return DateTime.fromISO(row.ExpectedSettleDate).toJSDate()
                },
                valueFormatter: (_value, row) => {
                    return DateTime.fromISO(row.ExpectedSettleDate).toFormat("dd/MM/yyyy");
                },
                flex: 1
            }, 
            {
                field: 'AgentName',
                headerName: 'Agent',
                flex: 1
            }
        ] as GridColDef[]

        return columns;
    }, [])

    return upcomingSettlementColumns;
}

export const useColumnGroupingModel = (templateType?: ReportTemplateTypes) => {
    const groupingModel = useMemo(() => {
        if(templateType == ReportTemplateTypes.GrossRealisationReport) {
            const model:GridColumnGroupingModel = [
                {
                    groupId: 'Short (1 year)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'ShortAverage'},
                        { field: 'ShortIncrease'}
                    ]
                },
                {
                    groupId: 'Medium (3 years)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'MediumAverage'},
                        { field: 'MediumIncrease'}
                    ]
                },
                {
                    groupId: 'Long (Project)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'LongAverage'},
                        { field: 'LongIncrease'}
                    ]
                }
            ];

            return model;
        }

        if(templateType == ReportTemplateTypes.SalesRateReport) {
            const model:GridColumnGroupingModel = [
                {
                    groupId: 'Short (1 year)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'ShortPa'},
                        { field: 'ShortMonth'}
                    ]
                },
                {
                    groupId: 'Medium (3 years)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'MediumPa'},
                        { field: 'MediumMonth'}
                    ]
                },
                {
                    groupId: 'Long (Project)',
                    description: '',
                    headerClassName: 'column-group-header',
                    children: [
                        { field: 'LongPa'},
                        { field: 'LongMonth'}
                    ]
                }
            ];

            return model;
        }

        return [];
    }, [templateType])

    return groupingModel;
}
